import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';

import { SaveUiPreferences } from '../../store/actions/ui-preferences.actions';
import { getUiPreferences$ } from '../../store/selectors/ui-preferences.selector';

export interface UserPreferences {
    theme?: 'light' | 'dark' | 'auto';
    language?: string;
    timezone?: string;
    dateFormat?: string;
    [key: string]: any;
}

@Injectable({
    providedIn: 'root',
})
export class UserPreferencesService {
    public static instance: UserPreferencesService | null = null;
    private readonly STORAGE_KEY = 'user_preferences';
    private readonly USER_KEY = 'user';
    private allUserPreferences: Record<string, UserPreferences> = {};
    private preferencesSubject: BehaviorSubject<UserPreferences>;
    private preferences$: Observable<UserPreferences>;
    /** Guards against re-entrant backend saves triggered by the store → subject loop */
    private _syncingFromBackend = false;

    constructor(private store$: Store) {
        UserPreferencesService.instance = this;
        this.allUserPreferences = this.loadFromStorage();
        this.preferencesSubject = new BehaviorSubject<UserPreferences>({});
        this.preferences$ = this.preferencesSubject.asObservable().pipe(distinctUntilChanged());
        this.loadCurrentUserPreferences();

        this.store$.select(getUiPreferences$).subscribe(backendPrefs => {
            if (backendPrefs) {
                this._syncingFromBackend = true;
                const current = this.preferencesSubject.value || {};
                this.preferencesSubject.next({ ...current, ...backendPrefs });
                this._syncingFromBackend = false;
            }
        });
    }

    private isBackendUiPreferenceKey(key: string): boolean {
        return key === 'defaultTenantId' || key === 'apps' || key.startsWith('apps.');
    }

    private getSuccessMessage(key: string): string {
        if (key === 'defaultTenantId') {
            return 'Default tenant updated successfully';
        }
        if (key === 'apps' || key.startsWith('apps.')) {
            return 'Closure search configuration updated successfully';
        }
        return 'Preferences saved successfully';
    }

    loadCurrentUserPreferences(): void {
        const currentUserId = this.getCurrentUserId();
        if (currentUserId) {
            const userPrefs = this.allUserPreferences[currentUserId] || {};
            this.preferencesSubject.next(userPrefs);
        } else {
            this.preferencesSubject.next({});
        }
    }

    setCurrentUser(userId: string): void {
        const currentUserId = this.getCurrentUserId();
        if (currentUserId === userId) {
            return;
        }

        try {
            const userObj = JSON.parse(localStorage.getItem(this.USER_KEY) || '{}');
            userObj.userId = userId;
            localStorage.setItem(this.USER_KEY, JSON.stringify(userObj));
        } catch (error) {
            console.error('Error setting user ID in localStorage:', error);
        }

        this.loadCurrentUserPreferences();
    }

    getCurrentUserId(): string | null {
        try {
            const userObj = JSON.parse(localStorage.getItem(this.USER_KEY) || '{}');
            return userObj.userId || null;
        } catch (error) {
            console.error('Error getting user ID from localStorage:', error);
            return null;
        }
    }

    clearCurrentUser(): void {
        try {
            const userObj = JSON.parse(localStorage.getItem(this.USER_KEY) || '{}');
            delete userObj.userId;
            localStorage.setItem(this.USER_KEY, JSON.stringify(userObj));
        } catch (error) {
            console.error('Error clearing user ID from localStorage:', error);
        }

        this.preferencesSubject.next({});
    }

    removeUserPreferences(userId: string): void {
        const remaining = { ...this.allUserPreferences };
        delete remaining[userId];
        this.allUserPreferences = remaining;
        this.saveToStorage();

        if (this.getCurrentUserId() === userId) {
            this.preferencesSubject.next({});
        }
    }

    getAllUserIds(): string[] {
        return Object.keys(this.allUserPreferences);
    }

    getPreferences$(): Observable<UserPreferences> {
        return this.preferences$;
    }

    getPreference$<T>(key: string, defaultValue?: T): Observable<T> {
        return this.preferences$.pipe(map(prefs => this.getNestedValue(prefs, key, defaultValue)));
    }

    getPreference<T>(key: string, defaultValue?: T): T {
        return this.getNestedValue(this.preferencesSubject.value, key, defaultValue);
    }

    setPreference<T>(key: string, value: T): void {
        if (!this.getCurrentUserId() || this._syncingFromBackend) {
            if (!this.getCurrentUserId()) {
                console.warn('Cannot set preference: No current user set');
            }
            return;
        }

        const currentPreferences = this.preferencesSubject.value;
        const updatedPreferences = this.setNestedValue(currentPreferences, key, value);

        if (this.isBackendUiPreferenceKey(key)) {
            this.preferencesSubject.next(updatedPreferences);
            const currentUserId = this.getCurrentUserId();
            if (currentUserId) {
                this.allUserPreferences[currentUserId] = updatedPreferences;
            }
            try {
                localStorage.removeItem(this.STORAGE_KEY);
            } catch {
                // ignore
            }

            let currentBackendPrefs: any = {};
            this.store$
                .select(getUiPreferences$)
                .subscribe(p => (currentBackendPrefs = p ?? {}))
                .unsubscribe();
            const updatedBackendPrefs = this.setNestedValue(currentBackendPrefs, key, value);
            this.store$.dispatch(SaveUiPreferences({ preferences: updatedBackendPrefs, successMessage: this.getSuccessMessage(key) }));
            return;
        }

        this.updatePreferences(updatedPreferences);
    }

    setPreferences(preferences: Partial<UserPreferences>): void {
        if (!this.getCurrentUserId() || this._syncingFromBackend) {
            if (!this.getCurrentUserId()) {
                console.warn('Cannot set preferences: No current user set');
            }
            return;
        }

        const currentPreferences = this.preferencesSubject.value;
        const updatedPreferences = { ...currentPreferences, ...preferences };

        const hasBackendKey = Object.keys(preferences).some(k => this.isBackendUiPreferenceKey(k));
        if (hasBackendKey) {
            this.preferencesSubject.next(updatedPreferences);
            const currentUserId = this.getCurrentUserId();
            if (currentUserId) {
                this.allUserPreferences[currentUserId] = updatedPreferences;
            }
            try {
                localStorage.removeItem(this.STORAGE_KEY);
            } catch {
                // ignore
            }

            let currentBackendPrefs: any = {};
            this.store$
                .select(getUiPreferences$)
                .subscribe(p => (currentBackendPrefs = p ?? {}))
                .unsubscribe();
            let updatedBackendPrefs = { ...currentBackendPrefs };
            for (const k of Object.keys(preferences)) {
                if (this.isBackendUiPreferenceKey(k)) {
                    updatedBackendPrefs = this.setNestedValue(updatedBackendPrefs, k, preferences[k]);
                }
            }
            const backendKeys = Object.keys(preferences).filter(k => this.isBackendUiPreferenceKey(k));
            const successMessage = backendKeys.length === 1 ? this.getSuccessMessage(backendKeys[0]) : 'Preferences saved successfully';
            this.store$.dispatch(SaveUiPreferences({ preferences: updatedBackendPrefs, successMessage }));
            return;
        }

        this.updatePreferences(updatedPreferences);
    }

    removePreference(key: string): void {
        if (!this.getCurrentUserId() || this._syncingFromBackend) {
            if (!this.getCurrentUserId()) {
                console.warn('Cannot remove preference: No current user set');
            }
            return;
        }

        const currentPreferences = this.preferencesSubject.value;
        const updatedPreferences = this.removeNestedValue(currentPreferences, key);

        if (this.isBackendUiPreferenceKey(key)) {
            this.preferencesSubject.next(updatedPreferences);
            const currentUserId = this.getCurrentUserId();
            if (currentUserId) {
                this.allUserPreferences[currentUserId] = updatedPreferences;
            }
            try {
                localStorage.removeItem(this.STORAGE_KEY);
            } catch {
                // ignore
            }

            let currentBackendPrefs: any = {};
            this.store$
                .select(getUiPreferences$)
                .subscribe(p => (currentBackendPrefs = p ?? {}))
                .unsubscribe();
            const updatedBackendPrefs = this.removeNestedValue(currentBackendPrefs, key);
            this.store$.dispatch(SaveUiPreferences({ preferences: updatedBackendPrefs, successMessage: this.getSuccessMessage(key) }));
            return;
        }

        this.updatePreferences(updatedPreferences);
    }

    clearPreferences(): void {
        if (!this.getCurrentUserId()) {
            console.warn('Cannot clear preferences: No current user set');
            return;
        }

        this.updatePreferences({});
    }

    resetPreferences(defaultPreferences: UserPreferences): void {
        if (!this.getCurrentUserId()) {
            console.warn('Cannot reset preferences: No current user set');
            return;
        }

        this.updatePreferences(defaultPreferences);
    }

    hasPreference(key: string): boolean {
        return this.hasNestedValue(this.preferencesSubject.value, key);
    }

    getKeys(): string[] {
        return Object.keys(this.preferencesSubject.value);
    }

    private updatePreferences(preferences: UserPreferences): void {
        this.preferencesSubject.next(preferences);

        const currentUserId = this.getCurrentUserId();
        if (currentUserId) {
            this.allUserPreferences[currentUserId] = preferences;
            this.saveToStorage();
        }
    }

    private loadFromStorage(): Record<string, UserPreferences> {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            console.error('Error loading preferences from localStorage:', error);
            return {};
        }
    }

    private saveToStorage(): void {
        try {
            // Strip backend UI preferences (defaultTenantId, apps) from localStorage snapshot
            const copyAll: Record<string, any> = {};
            let hasAnyPref = false;
            for (const uid of Object.keys(this.allUserPreferences)) {
                if (this.allUserPreferences[uid]) {
                    const rest = { ...this.allUserPreferences[uid] };
                    delete rest.defaultTenantId;
                    delete rest.apps;
                    if (Object.keys(rest).length > 0) {
                        copyAll[uid] = rest;
                        hasAnyPref = true;
                    }
                }
            }
            if (hasAnyPref) {
                localStorage.setItem(this.STORAGE_KEY, JSON.stringify(copyAll));
            } else {
                localStorage.removeItem(this.STORAGE_KEY);
            }
        } catch (error) {
            console.error('Error saving preferences to localStorage:', error);
        }
    }

    private getNestedValue<T>(obj: any, key: string, defaultValue?: T): T {
        const keys = key.split('.');
        let result = obj;

        for (const k of keys) {
            if (result == null || typeof result !== 'object') {
                return defaultValue as T;
            }
            result = result[k];
        }

        return result !== undefined ? result : (defaultValue as T);
    }

    private setNestedValue<T>(obj: any, key: string, value: T): any {
        const keys = key.split('.');
        if (keys.length === 1) {
            return { ...obj, [key]: value };
        }

        const [firstKey, ...remainingKeys] = keys;
        const nestedObj = obj[firstKey] || {};
        const updatedNested = this.setNestedValue(nestedObj, remainingKeys.join('.'), value);

        return { ...obj, [firstKey]: updatedNested };
    }

    private removeNestedValue(obj: any, key: string): any {
        const keys = key.split('.');
        if (keys.length === 1) {
            const remaining = { ...obj };
            delete remaining[key];
            return remaining;
        }

        const [firstKey, ...remainingKeys] = keys;
        if (obj[firstKey] == null) {
            return obj;
        }

        const updatedNested = this.removeNestedValue(obj[firstKey], remainingKeys.join('.'));

        if (Object.keys(updatedNested).length === 0) {
            const remaining = { ...obj };
            delete remaining[firstKey];
            return remaining;
        }

        return { ...obj, [firstKey]: updatedNested };
    }

    private hasNestedValue(obj: any, key: string): boolean {
        const keys = key.split('.');
        let result = obj;

        for (const k of keys) {
            if (result == null || typeof result !== 'object') {
                return false;
            }
            if (!(k in result)) {
                return false;
            }
            result = result[k];
        }

        return true;
    }
}
